import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ImpersonationActive,
  ImpersonationState,
  ImpersonationTargets,
} from '../models/impersonation';

/**
 * Server-side "Ver como" session of an internal user. The API answers every
 * request as the target while the session is active, so after a change the
 * page is reloaded instead of patching the state held in memory.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly url = `${environment.apiUrl}/account/impersonation`;
  private readonly targetsUrl = `${environment.apiUrl}/api/ops/impersonation/targets`;

  private readonly activeSource = new BehaviorSubject<ImpersonationActive | null>(null);
  /** The active session, or null when there is none. */
  readonly active$ = this.activeSource.asObservable();

  constructor(private http: HttpClient) {}

  get activeValue(): ImpersonationActive | null {
    return this.activeSource.value;
  }

  /** Reads the session from the API; used on load and when the session may have expired. */
  load(): Observable<ImpersonationState> {
    return this.http
      .get<ImpersonationState>(this.url)
      .pipe(tap((state) => this.activeSource.next(state.active ? state : null)));
  }

  targets(): Observable<ImpersonationTargets> {
    return this.http.get<ImpersonationTargets>(this.targetsUrl);
  }

  /** `accessLevel`: the level to view with, to try each level's dashboards; omitted, the pharmacies' own. */
  startPharmacies(codigosAnf: number[], accessLevel?: number | null): Observable<ImpersonationActive> {
    return this.start(accessLevel == null ? { codigosAnf } : { codigosAnf, accessLevel });
  }

  startLab(contactoLabId: number): Observable<ImpersonationActive> {
    return this.start({ contactoLabId });
  }

  end(): Observable<void> {
    return this.http
      .delete<void>(this.url)
      .pipe(tap(() => this.activeSource.next(null)));
  }

  /** Full page load, so every page reads the data of the new view. */
  goTo(url: string): void {
    window.location.assign(url);
  }

  reload(): void {
    window.location.reload();
  }

  private start(body: { codigosAnf: number[]; accessLevel?: number } | { contactoLabId: number }): Observable<ImpersonationActive> {
    return this.http
      .post<ImpersonationActive>(this.url, body)
      .pipe(tap((state) => this.activeSource.next(state)));
  }
}
